import { observer } from "mobx-react-lite";
import { Switch as SwitchPrimitive } from "radix-ui";

import { cn } from "@/utils/shadcn-ui";

import { useFormFieldId } from "../form/form-field-id-context";
import { FieldProps, useModelBinding } from "./utils";

interface Props<M extends object> extends FieldProps<M, boolean> {
  disabled?: boolean;
  className?: string;
  size?: "sm" | "default";
  /** For switches outside a form field, where no `<label>` names them. */
  ariaLabel?: string;
}

export const Switch = observer(function Switch<M extends object>({
  disabled,
  className,
  size = "default",
  ariaLabel,
  ...restProps
}: Props<M>) {
  const formFieldId = useFormFieldId();
  const { value, onChange } = useModelBinding(restProps);

  return (
    <SwitchPrimitive.Root
      id={formFieldId}
      data-slot="switch"
      data-size={size}
      className={cn(
        "peer group/switch inline-flex shrink-0 items-center rounded-full border border-transparent p-0 shadow-xs transition-all outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:opacity-50 data-[size=default]:h-5 data-[size=default]:w-9 data-[size=sm]:h-3.5 data-[size=sm]:w-6 data-[state=checked]:bg-primary data-[state=unchecked]:bg-input dark:data-[state=unchecked]:bg-input/80",
        className,
      )}
      checked={!!value}
      onCheckedChange={onChange}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <SwitchPrimitive.Thumb
        data-slot="switch-thumb"
        className="pointer-events-none block rounded-full bg-background ring-0 transition-transform group-data-[size=default]/switch:size-4 group-data-[size=sm]/switch:size-3 data-[state=unchecked]:translate-x-[1px] group-data-[size=default]/switch:data-[state=checked]:translate-x-[17px] group-data-[size=sm]/switch:data-[state=checked]:translate-x-[9px] dark:data-[state=checked]:bg-primary-foreground dark:data-[state=unchecked]:bg-foreground"
      />
    </SwitchPrimitive.Root>
  );
});
